import { useState } from "react";
import { describeError } from "../api/errors";
import { useApi } from "../api/context";
import { Icon } from "../components/Icon";
import { Sheet } from "../components/Sheet";
import { t } from "../i18n";
import { formatCents } from "../lib/money";
import { go } from "../lib/router";
import { useHeroData } from "../state/heroData";

/** Суммы подарка по продуктовому описанию: $3, $10, $25. */
export const GIFT_AMOUNTS = [300, 1000, 2500] as const;

/**
 * Подарок герою — чаевые креатору. Сразу говорим, что он ничего не открывает: иначе
 * человек заплатит, ожидая следующую серию, и будет прав, требуя деньги назад.
 * Оплата — Stripe Checkout (TipResponse.url); спасибо говорит «Плеер» по возвращении.
 */
export function GiftSheet({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const { hero } = useHeroData();
  const [amount, setAmount] = useState<number>(GIFT_AMOUNTS[1]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currency = hero.pricing.currency;

  async function send() {
    setSending(true);
    setError(null);
    try {
      const message = note.trim();
      const { url } = await api.tip({ amountCents: amount, ...(message ? { message } : {}) });
      // Внешний адрес (Stripe) уводит со страницы; свой (мок) — шторка закрывается сама
      onClose();
      go(url);
    } catch (tipError) {
      setError(describeError(tipError, "tip"));
      setSending(false);
    }
  }

  return (
    <Sheet title={t("gift.title", { hero: hero.name })} onClose={onClose}>
      <div className="gift">
        <p className="muted">{t("gift.body", { hero: hero.name, creator: hero.creator.name })}</p>
        <div className="amounts" role="radiogroup" aria-label={t("gift.amount")}>
          {GIFT_AMOUNTS.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={amount === value}
              className={`amount${amount === value ? " amount--selected" : ""}`}
              onClick={() => setAmount(value)}
            >
              {formatCents(value, currency).replace(/\.00$/, "")}
            </button>
          ))}
        </div>
        <label className="field">
          <span>{t("gift.note")}</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, 200))}
            maxLength={200}
            rows={2}
            placeholder={t("gift.notePlaceholder")}
          />
        </label>
        {error && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
        <button type="button" className="btn btn--primary btn--lg btn--block" onClick={() => void send()} disabled={sending}>
          <Icon name="gift" size={18} />
          {t("gift.send", { amount: formatCents(amount, currency) })}
        </button>
      </div>
    </Sheet>
  );
}
